/**
 * Account payments screen — pure view-model helpers (no React, no side effects).
 */
import type { TFunction } from 'i18next';
import type { PaymentHistoryItemDTO } from '../accountTypes.js';

export type AccountPaymentsScreenPhase = 'loading' | 'error' | 'empty' | 'populated';

export function isAwaitingQrPayment(item: PaymentHistoryItemDTO): boolean {
  if (item.status !== 'INITIATED' && item.status !== 'PENDING') {
    return false;
  }
  return item.qrVisible !== false;
}

export function isCancellablePendingPayment(item: PaymentHistoryItemDTO): boolean {
  return item.status === 'INITIATED' || item.status === 'PENDING';
}

export function resolvePaymentPurposeLabel(
  t: TFunction<'customer'>,
  purposeType: string,
): string {
  const key = `account.payments.purpose.${purposeType}`;
  const label = t(key);
  return label === key ? purposeType : label;
}

export function resolvePaymentStatusLabel(t: TFunction<'customer'>, status: string): string {
  const key = `account.payments.status.${status}`;
  const label = t(key);
  return label === key ? t('account.orders.statusUnknown') : label;
}

export function resolveAccountPaymentsPhase(input: {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly pendingCount: number;
  readonly historyCount: number;
}): AccountPaymentsScreenPhase {
  if (input.isLoading) {
    return 'loading';
  }
  if (input.isError) {
    return 'error';
  }
  if (input.pendingCount === 0 && input.historyCount === 0) {
    return 'empty';
  }
  return 'populated';
}

export function historyHasReceiptRow(items: readonly PaymentHistoryItemDTO[]): boolean {
  return items.some((item) => item.receiptAvailability !== 'NONE');
}
